import { useState, useMemo } from "react";
import { JOURNALISM, IMAGES, IMAGE_CAPTIONS, AWARDS } from "../lib/content";
import { Award } from "lucide-react";
import ZoomableImage from "../components/site/ZoomableImage";

const ALL = "All";

export default function Journalism() {
  const [filter, setFilter] = useState(ALL);

  const categories = useMemo(() => {
    return [ALL, ...Array.from(new Set(JOURNALISM.map((a) => a.category)))];
  }, []);

  const list = useMemo(() => {
    return filter === ALL ? JOURNALISM : JOURNALISM.filter((a) => a.category === filter);
  }, [filter]);

  return (
    <div data-testid="journalism-page" className="bg-cream">
      {/* Hero */}
      <section className="container-vk pt-20 md:pt-28 pb-20 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <p className="overline mb-6">Journalism</p>
          <h1 className="heading-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight">
            A decade of reporting from{" "}
            <span className="italic text-burgundy">inside the rooms</span> where
            decisions are made.
          </h1>
          <p className="mt-8 text-lg text-ink/70 max-w-2xl leading-relaxed">
            Presidential coverage. Governance investigations. Climate and public
            health reporting. Award-winning features written for readers who
            deserve more than the press release.
          </p>
        </div>
        <div className="lg:col-span-5">
          <ZoomableImage
            src={IMAGES.journalismScene}
            alt="Victor Kwawukume questioning at a national press conference"
            captionOverline="Public Affairs · Press Conference"
            caption={IMAGE_CAPTIONS.journalismScene}
            aspect="aspect-[4/3]"
            testId="journalism-hero-image"
          />
        </div>
      </section>

      {/* PRESIDENTIAL CORRESPONDENT — Professional Highlight */}
      <section
        data-testid="presidential-correspondent-section"
        className="bg-cream-deep border-y border-paperline py-24 md:py-32 mb-12"
      >
        <div className="container-vk grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <ZoomableImage
              src={IMAGES.presidentialPhoto}
              alt="Victor Kwawukume with H.E. President Nana Akufo-Addo of Ghana"
              captionOverline="Jubilee House · Accra"
              caption={IMAGE_CAPTIONS.presidentialPhoto}
              aspect="aspect-[4/3]"
              testId="presidential-correspondent-image"
            />
          </div>
          <div className="lg:col-span-6">
            <p className="overline mb-5">Professional Highlight</p>
            <h2 className="heading-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05] text-balance">
              Presidential Correspondent &mdash;{" "}
              <span className="italic text-burgundy">
                reporting from the highest levels of government
              </span>
              .
            </h2>
            <div className="mt-6 space-y-5 text-ink/80 leading-relaxed max-w-xl">
              <p>
                For years Victor covered the Presidency of Ghana &mdash; cabinet
                announcements, foreign visits, parliamentary addresses and the
                policy decisions that travel a long distance from the briefing
                room to the citizen.
              </p>
              <p>
                That assignment shaped his standard for the work: power must be
                questioned plainly, and the public must always be the audience
                the questions are written for.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="container-vk pb-10" data-testid="journalism-filters">
        <div className="flex flex-wrap gap-2 border-t border-paperline pt-6">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s|&/g, "-")}`}
              className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                filter === c
                  ? "bg-ink text-cream border-ink"
                  : "border-paperline text-ink/70 hover:border-burgundy hover:text-burgundy"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Article list */}
      <section className="container-vk pb-24">
        <ul className="divide-y divide-paperline border-y border-paperline" data-testid="journalism-list">
          {list.map((a, i) => (
            <li
              key={a.title}
              data-testid={`article-${i}`}
              className="grid md:grid-cols-12 gap-6 md:gap-10 py-10 group hover:bg-cream-deep/40 transition-colors px-2 md:px-4"
            >
              <div className="md:col-span-2">
                <p className="heading-display text-4xl font-semibold text-burgundy">
                  {a.year}
                </p>
                <p className="overline mt-1">{a.category}</p>
              </div>
              <div className="md:col-span-10">
                <h2 className="heading-display text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight">
                  {a.title}
                </h2>
                <p className="mt-2 italic font-serif text-ink/60">
                  {a.publication}
                </p>
                <p className="mt-4 text-ink/80 max-w-3xl leading-relaxed">
                  {a.excerpt}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Awards */}
      <section className="bg-cream-deep py-20 md:py-24" data-testid="journalism-awards">
        <div className="container-vk">
          <div className="flex items-center gap-3 mb-6">
            <Award size={20} className="text-gold" />
            <p className="overline">Recognition</p>
          </div>
          <h2 className="heading-display text-3xl md:text-4xl font-semibold max-w-3xl mb-10">
            Honoured by the colleagues whose judgement matters most.
          </h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {AWARDS.map((a) => (
              <li
                key={a}
                className="border border-paperline p-6 text-sm uppercase tracking-[0.14em] text-ink/75 leading-relaxed bg-cream"
              >
                {a}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
